/*
 * Purpose: Production auth overlay — sign-in, tenant pick, plan selection on protected routes
 * Last modified: 2026-07-01 | Agent
 * Completeness: 100
 */
import { Outlet, useLocation, Link } from 'react-router-dom';
import type { ReactNode } from 'react';
import { AUTH_CONFIG } from '@/lib/auth-config';
import { isProtectedRoute } from '@/lib/primaryNav';
import { useAuthStore } from '@/stores/authStore';
import { PasswordlessSignInPanel } from '@/components/auth/PasswordlessSignInPanel';
import { TenantPickerPanel } from '@/components/auth/TenantPickerPanel';
import { PlanSelectionPanel } from '@/components/auth/PlanSelectionPanel';
import { BrandMark } from '@/components/brand/BrandMark';
import { PRODUCT_NAME } from '@/lib/productTerms';
import '@/styles/auth-flow.css';

function GateChrome({ children }: { children: ReactNode }) {
  return (
    <div className="auth-gate-overlay min-h-screen flex flex-col bg-[var(--auth-bg)]">
      <header className="shrink-0 border-b border-[var(--auth-border)]/60 px-4 py-3 flex items-center gap-2">
        <Link to="/" className="flex items-center gap-2">
          <BrandMark size={24} ghostAccent="darker" />
          <span className="ab-brand-wordmark text-sm">{PRODUCT_NAME}</span>
        </Link>
      </header>
      <main className="flex-1 flex items-center justify-center p-4 sm:p-8">{children}</main>
    </div>
  );
}

export function AuthGateOverlay() {
  const location = useLocation();
  const ready = useAuthStore((s) => s.ready);
  const authed = useAuthStore((s) => s.authed);
  const tenantPickRequired = useAuthStore((s) => s.tenantPickRequired);
  const planPickRequired = useAuthStore((s) => s.planPickRequired);

  if (!ready) {
    return (
      <div className="min-h-screen flex items-center justify-center text-muted-foreground text-sm">
        Checking session…
      </div>
    );
  }

  const protectedRoute = isProtectedRoute(location.pathname);
  const next = location.pathname + location.search;

  if (!protectedRoute) {
    return <Outlet />;
  }

  if (!AUTH_CONFIG.mock && !authed) {
    return (
      <GateChrome>
        <PasswordlessSignInPanel next={next} />
      </GateChrome>
    );
  }

  if (authed && tenantPickRequired) {
    return (
      <GateChrome>
        <TenantPickerPanel next={next} />
      </GateChrome>
    );
  }

  if (authed && planPickRequired) {
    return (
      <GateChrome>
        <PlanSelectionPanel next={next} />
      </GateChrome>
    );
  }

  return <Outlet />;
}

export default AuthGateOverlay;
